import messaging, { type FirebaseMessagingTypes } from "@react-native-firebase/messaging";
import { Platform } from "react-native";

export type AuthorizationStatus = FirebaseMessagingTypes.AuthorizationStatus;

export type RemoteMessage = FirebaseMessagingTypes.RemoteMessage;

/**
 * Re-export authorization status constants for comparison without importing
 * the full Firebase messaging module in consuming code.
 */
export const AUTHORIZATION_STATUS = {
  NOT_DETERMINED: messaging.AuthorizationStatus.NOT_DETERMINED,
  DENIED: messaging.AuthorizationStatus.DENIED,
  AUTHORIZED: messaging.AuthorizationStatus.AUTHORIZED,
  PROVISIONAL: messaging.AuthorizationStatus.PROVISIONAL,
} as const;

export async function requestPermission(): Promise<AuthorizationStatus> {
  const status = await messaging().requestPermission();
  return status;
}

export async function getPermissionStatus(): Promise<AuthorizationStatus> {
  return messaging().hasPermission();
}

export async function getToken(): Promise<string> {
  if (Platform.OS === "ios" && !messaging().isDeviceRegisteredForRemoteMessages) {
    await messaging().registerDeviceForRemoteMessages();
  }
  return messaging().getToken();
}

export async function deleteToken(): Promise<void> {
  return messaging().deleteToken();
}

/**
 * Tell the OS to stop delivering remote notifications to this app.
 * Local operation — works offline. Call `registerDeviceForRemoteMessages()`
 * (done automatically by `getToken()`) to re-enable on next login.
 */
export async function unregisterDeviceForRemoteMessages(): Promise<void> {
  if (Platform.OS === "ios") {
    await messaging().unregisterDeviceForRemoteMessages();
  }
}

export function onTokenRefresh(callback: (token: string) => void): () => void {
  return messaging().onTokenRefresh(callback);
}

export function onForegroundMessage(callback: (message: RemoteMessage) => void): () => void {
  return messaging().onMessage(callback);
}

export function setBackgroundMessageHandler(handler: (message: RemoteMessage) => Promise<void>): void {
  messaging().setBackgroundMessageHandler(handler);
}

export async function getInitialNotification(): Promise<RemoteMessage | null> {
  return messaging().getInitialNotification();
}

export function onNotificationOpenedApp(callback: (message: RemoteMessage) => void): () => void {
  return messaging().onNotificationOpenedApp(callback);
}

export function getDevicePlatform(): "ios" | "android" {
  return Platform.OS === "ios" ? "ios" : "android";
}
